import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { ReactNode } from 'react';

/**
 * The vertical step every screen is built on — ADMIN-11.
 *
 * One number rather than a value per screen. The console had five screens on
 * `spacing={2}` and five on `spacing={3}`, and two that padded themselves *again*
 * inside the shell's own padding, so which screen you were on changed how far the
 * content sat from the drawer. `docs/decisions/admin-page-header.md` has the rest.
 */
export const SCREEN_GAP = 3;

/**
 * The top of every list, detail and form screen in the back-office.
 *
 * It is here rather than repeated because sixteen routes had sixteen arrangements:
 * `h4` on one, `h5` on the rest, a raw `<h1>` on `NotFound`, and a `Chip` nested
 * inside the heading text on the job screen. Only two of them named a semantic
 * level, so most screens had no `h1` for a screen reader to land on.
 *
 * The action slot is whatever the screen puts opposite its title — a New button, a
 * status chip, the signed-in account. It is a slot rather than a `button` prop
 * because those three are not the same kind of thing.
 */
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Stack direction="row" spacing={2} sx={ROW}>
      <Box sx={TITLES}>
        <Typography variant="h5" component="h1">
          {title}
        </Typography>
        {subtitle !== undefined && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {action !== undefined && <Box sx={ASIDE}>{action}</Box>}
    </Stack>
  );
}

const ROW = {
  justifyContent: 'space-between',
  alignItems: 'flex-start',
} as const;
const TITLES = { minWidth: 0 } as const;
const ASIDE = { flexShrink: 0 } as const;
